import logger from '@/modules/Logger';
import {
  BUFFER_SWITCH_DOWNGRADE_COUNT,
  BUFFER_SWITCH_UPGRADE_COUNT,
  STREAM_TYPE
} from '@/modules/webrtc/agora/const';
import store from '@/store/index';

let newDownlinkQuality = {};

export default {
  async setQualityOnDownLinkSpeed(quality) {
    newDownlinkQuality = this.setQualityOfNetwork(quality);

    if (this.downLinkNetworkQuality === newDownlinkQuality.value) {
      return;
    }

    if (this.isWithinDownLinkBufferLimit()) {
      this.switchDownLinkBuffer.push(newDownlinkQuality.value);
      return;
    }

    await this.setReceiveStreamQuality();
  },

  async setReceiveStreamQuality() {
    this.switchDownLinkBuffer = [];
    this.downLinkNetworkQuality = newDownlinkQuality.value;

    if (!this.rtc.remoteUsers.length || !this.rtc.remoteUsers[0].hasVideo) {
      logger.debug('No remote user on the channel or remote user has no video track');
      return;
    }

    logger.info('[Agora] Downlink network changes, switch receive stream quality', {
      operation: 'webrtc:call-quality',
      quality: newDownlinkQuality.label
    });

    try {
      if (this.isDownLinkPoorQuality() && process.env.VUE_APP_AGORA_ENABLE_AUDIO_ONLY === 'true') {
        logger.error('Detected downlink network quality is poor', {
          quality: newDownlinkQuality.label,
          event_type: 49,
          lesson_id: store.state.lessonId
        });
        await this.switchToAudioOnly();
      } else {
        await this.adjustRemoteVideoQuality();
      }
    } catch (error) {
      logger.error('[Agora] Error while setting receive stream quality', {
        operation: 'webrtc:call-quality',
        error: error.message,
        event_type: 49,
        lesson_id: this.lessonId
      });
    }
  },

  // on poor network quality, switch to AUDIO_ONLY
  async switchToAudioOnly() {
    logger.info('[Agora] Switch to AUDIO ONLY');
    await this.rtc.unsubscribe(this.rtc.remoteUsers[0], 'video');
    store.commit('videoRtc/setAudioOnly', true);
  },

  // on excellent or moderate network quality, adjust remote video stream resolution
  async adjustRemoteVideoQuality() {
    const remoteUser = this.rtc.remoteUsers[0];
    const wasAudioOnly = store.state.videoRtc.audioOnly;

    await this.rtc.subscribe(remoteUser, 'video');

    // After recovering from audio-only, the previous unsubscribe ended the old MediaStreamTrack.
    // Rebuild the MediaStream with the fresh track so the <video> element renders it.
    if (wasAudioOnly && remoteUser.videoTrack) {
      const uid = remoteUser.uid;

      // Update remoteUserTrack with the fresh video track (matching user-published behavior)
      this.remoteUserTrack = this.remoteUserTrack.filter(item => item.trackMediaType !== 'video');
      this.remoteUserTrack.push(remoteUser.videoTrack);

      // Remove old entry and rebuild stream from all current tracks (new instance so Vue re-assigns srcObject)
      this.remoteUserExists(uid);
      const remoteStream = new MediaStream();
      this.remoteUserTrack.forEach(track => {
        const mediaTrack = track.getMediaStreamTrack();
        if (mediaTrack) {
          remoteStream.addTrack(mediaTrack);
        }
      });
      this.remoteUsers.push({ peerId: uid, stream: remoteStream });

      logger.info('[Agora] Rebuilt remote stream after audio-only recovery', {
        operation: 'webrtc:call-quality',
        lesson_id: this.lessonId
      });
    }

    // Lift audio-only overlay after the stream is rebuilt to avoid showing a black frame
    store.commit('videoRtc/setAudioOnly', false);

    // Rebuild the MediaStream with the new video track so Vue's srcObject binding updates
    if (remoteUser.videoTrack && this.remoteUsers.length) {
      const newStream = new MediaStream();
      // Add audio track if available
      if (remoteUser.audioTrack) {
        newStream.addTrack(remoteUser.audioTrack.getMediaStreamTrack());
      }
      // Add the newly subscribed video track
      newStream.addTrack(remoteUser.videoTrack.getMediaStreamTrack());
      // Replace the stream reference so Vue reactivity picks up the change
      this.remoteUsers[0].stream = newStream;
    }

    const videoQuality = this.isDownLinkExcellentQuality() ? STREAM_TYPE.high : STREAM_TYPE.low;
    await this.rtc.setRemoteVideoStreamType(this.remoteUserId, videoQuality);
  },

  /*
   * Apply asymmetric buffering to minimize frequent switching.
   * Downgrade (quality worsening): triggers after BUFFER_SWITCH_DOWNGRADE_COUNT (3) consecutive reports
   * Upgrade (quality improving): triggers after BUFFER_SWITCH_UPGRADE_COUNT (6) consecutive reports
   */
  isWithinDownLinkBufferLimit() {
    const isDowngrade = newDownlinkQuality.value > this.downLinkNetworkQuality;
    const bufferThreshold = isDowngrade || this.isDownLinkPoorQuality() ? BUFFER_SWITCH_DOWNGRADE_COUNT : BUFFER_SWITCH_UPGRADE_COUNT;

    // Recover faster when coming from audio-only mode
    return this.switchDownLinkBuffer.length < bufferThreshold;
  }
};
